'use client'

import { ProductAttributeKey } from '@/lib/types/core/product-attribute-keys'
import { Button } from '@heroui/react'
import { IProductVariant } from '@/lib/types/core/product-variant'
import type { IMediaItem } from '@/lib/types/media'

// --- Color hex map ---

const COLOR_HEX_MAP: Record<string, string> = {
  black: '#000000',
  white: '#ffffff',
  red: '#cc0000',
  blue: '#000066',
  green: '#006600',
  yellow: '#ffcc00',
  orange: '#ff6600',
  purple: '#660099',
  pink: '#ff66b2',
  gray: '#888888',
  grey: '#888888',
  silver: '#c0c0c0',
  gold: '#ffd700',
  bronze: '#cd7f32',
  charcoal: '#444444',
  navy: '#001f3f',
  teal: '#008080',
  burgundy: '#800020',
  beige: '#f5f5dc',
  clear: '#e0e0e0',
}

// --- Helpers ---

export function extractColorName(variant: IProductVariant): string {
  // 1. attributeValues.COLOR
  const attrValues = variant.attributeValues as Record<string, any> | null | undefined
  const attrColor = attrValues?.[ProductAttributeKey.COLOR]
  if (typeof attrColor === 'string' && attrColor.trim()) {
    return attrColor.trim()
  }

  // 2. variant.color
  if (typeof variant.color === 'string' && variant.color.trim()) {
    return variant.color.trim()
  }

  // 3. First word of variant.name
  const firstWord = variant.name?.split(/\s+/)[0]
  return firstWord || 'Unknown'
}

/**
 * A variant set with no colour anywhere is width-axis: film sold in roll
 * widths (clear PPF). The selector then speaks in widths, not colours.
 */
export function isWidthAxis(variants: IProductVariant[]): boolean {
  return (
    variants.length > 0 &&
    variants.every(v => {
      const attrValues = v.attributeValues as Record<string, any> | null | undefined
      const hasColor = !!attrValues?.[ProductAttributeKey.COLOR] || !!v.color
      const hasWidth = attrValues?.[ProductAttributeKey.WIDTH] != null && attrValues[ProductAttributeKey.WIDTH] !== ''
      // Only an explicit WIDTH on every variant (and colour on none) flips the
      // axis; attribute-less legacy variants keep the colour wording.
      return !hasColor && hasWidth
    })
  )
}

export function extractOptionLabel(variant: IProductVariant, widthAxis: boolean): string {
  if (widthAxis) {
    const attrValues = variant.attributeValues as Record<string, any> | null | undefined
    const width = attrValues?.[ProductAttributeKey.WIDTH]
    if (width != null && width !== '') return `${width}\u2033`
    return variant.name || 'Unknown'
  }
  return extractColorName(variant)
}

/**
 * First media image attached to the variant, if any. Real supplier swatch
 * photography wins over any hex fill. Prisma returns `media` as an array even
 * though the shared type declares a single object, so accept both shapes.
 */
export function swatchMediaUrl(variant: IProductVariant): string | null {
  const m = (variant as { media?: unknown }).media
  if (Array.isArray(m)) return (m[0] as { url?: string } | undefined)?.url ?? null
  return (m as { url?: string } | null | undefined)?.url ?? null
}

/**
 * Full media item(s) attached to the variant, for the main preview (not just
 * the swatch chip URL). Same array-or-object tolerance as swatchMediaUrl:
 * the data layer normalizes to a single object, but this stays defensive
 * against the underlying Prisma array shape.
 */
export function variantMediaItems(variant: IProductVariant): IMediaItem[] {
  const m = (variant as { media?: unknown }).media
  const first = Array.isArray(m) ? m[0] : m
  return first ? [first as IMediaItem] : []
}

export function resolveColorHex(colorName: string): string {
  const lower = colorName.toLowerCase()

  // Exact match
  if (COLOR_HEX_MAP[lower]) {
    return COLOR_HEX_MAP[lower]
  }

  // Substring match: check if any key appears in the color name
  for (const [key, hex] of Object.entries(COLOR_HEX_MAP)) {
    if (lower.includes(key)) {
      return hex
    }
  }

  // Fallback gray
  return '#888888'
}

// --- Types ---

interface VariantInventoryEntry {
  variantId: string
  inventory: number
  inStock: boolean
}

export interface ColorSwatchRailProps {
  variants: IProductVariant[]
  selectedVariantId: string | null
  variantInventory: VariantInventoryEntry[]
  onSelectColor: (variantId: string) => void
  onShowAll: () => void
  maxVisible?: number
}

// --- Sorting ---

type StockBucket = 'in-stock' | 'low-stock' | 'out-of-stock'

function getStockBucket(inventory: number, inStock: boolean): StockBucket {
  if (!inStock || inventory <= 0) return 'out-of-stock'
  if (inventory >= 1 && inventory <= 5) return 'low-stock'
  return 'in-stock'
}

const BUCKET_ORDER: Record<StockBucket, number> = {
  'in-stock': 0,
  'low-stock': 1,
  'out-of-stock': 2,
}

function sortVariants(
  variants: IProductVariant[],
  variantInventory: VariantInventoryEntry[],
  widthAxis: boolean
): IProductVariant[] {
  const inventoryMap = new Map(variantInventory.map(v => [v.variantId, v]))

  return [...variants].sort((a, b) => {
    const invA = inventoryMap.get(a.id)
    const invB = inventoryMap.get(b.id)
    const bucketA = getStockBucket(invA?.inventory ?? 0, invA?.inStock ?? false)
    const bucketB = getStockBucket(invB?.inventory ?? 0, invB?.inStock ?? false)

    const orderDiff = BUCKET_ORDER[bucketA] - BUCKET_ORDER[bucketB]
    if (orderDiff !== 0) return orderDiff

    if (widthAxis) {
      return parseFloat(extractOptionLabel(a, true)) - parseFloat(extractOptionLabel(b, true))
    }
    const nameA = extractColorName(a).toLowerCase()
    const nameB = extractColorName(b).toLowerCase()
    return nameA.localeCompare(nameB)
  })
}

// --- Component ---

export function ColorSwatchRail({
  variants,
  selectedVariantId,
  variantInventory,
  onSelectColor,
  onShowAll,
  maxVisible = 6,
}: ColorSwatchRailProps) {
  const widthAxis = isWidthAxis(variants)
  const inventoryMap = new Map(variantInventory.map(v => [v.variantId, v]))

  // Out-of-stock colours/widths aren't offered at all, not shown-but-disabled.
  const inStockVariants = variants.filter(v => {
    const inv = inventoryMap.get(v.id)
    return getStockBucket(inv?.inventory ?? 0, inv?.inStock ?? false) !== 'out-of-stock'
  })
  const sorted = sortVariants(inStockVariants, variantInventory, widthAxis)
  const visible = sorted.slice(0, maxVisible)
  const hasOverflow = inStockVariants.length > maxVisible

  const selectedColorName = selectedVariantId
    ? extractOptionLabel(variants.find(v => v.id === selectedVariantId) ?? variants[0], widthAxis)
    : null

  return (
    <div className="flex flex-col gap-2">
      {/* Label */}
      <p className="text-sm text-muted">
        {widthAxis ? 'Width:' : 'Color:'}{' '}
        <strong className="text-foreground">
          {selectedColorName || (widthAxis ? 'Select a width' : 'Select a color')}
        </strong>
      </p>

      {/* Swatch rail */}
      <div className="flex items-center gap-2 flex-wrap">
        {visible.map(variant => {
          const colorName = extractOptionLabel(variant, widthAxis)
          // Real supplier swatch photography wins; swatchHex is the fallback,
          // and the keyword map only covers legacy variants without one.
          const mediaUrl = swatchMediaUrl(variant)
          const hex = variant.swatchHex ?? resolveColorHex(colorName)
          const inv = inventoryMap.get(variant.id)
          const isSelected = variant.id === selectedVariantId
          const isLowStock = (inv?.inventory ?? 0) >= 1 && (inv?.inventory ?? 0) <= 5

          return (
            <div key={variant.id} className="relative">
              <button
                type="button"
                data-testid={`variant-swatch-${variant.id}`}
                className={[
                  widthAxis
                    ? 'h-9 min-w-9 px-2 rounded border-2 text-sm font-medium transition-colors relative cursor-pointer'
                    : 'w-9 h-9 rounded border-2 transition-colors relative cursor-pointer',
                  isSelected
                    ? 'border-accent'
                    : 'border-divider hover:border-accent/60',
                ].join(' ')}
                style={widthAxis ? undefined : { backgroundColor: hex, overflow: 'hidden' }}
                onClick={() => onSelectColor(variant.id)}
                aria-label={colorName}
                aria-pressed={isSelected}
                title={colorName}
              >
                {!widthAxis && mediaUrl && (

                  <img
                    src={mediaUrl}
                    alt=''
                    aria-hidden='true'
                    className='absolute inset-0 h-full w-full rounded-[inherit] object-cover'
                  />
                )}
                {widthAxis && colorName}
              </button>

              {/* Low stock badge */}
              {isLowStock && (
                <span className="absolute -top-1.5 -right-1.5 flex items-center justify-center w-4 h-4 rounded-full bg-warning text-[10px] font-bold text-foreground leading-none">
                  {inv?.inventory}
                </span>
              )}
            </div>
          )
        })}

        {/* Overflow button. The tertiary fill (--default) is a 1.05 ratio
            against the page background, so without the hairline this sat among
            the colour swatches as floating text rather than a button. */}
        {hasOverflow && (
          <Button
            variant="tertiary"
            size="sm"
            onPress={onShowAll}
            className="text-sm text-foreground border border-border whitespace-nowrap"
          >
            See all {variants.length} &rarr;
          </Button>
        )}
      </div>
    </div>
  )
}
